// Shared scope picker for admin → integrations forms.
//
// Writes `{scopeType, scopeId}` into the caller's draft state (via
// `draftKey` — the name of the root state slot the page already uses,
// e.g. `_newSupabaseDraft` for the Supabase page). The caller then
// forwards those two fields to `saveIntegration({...draft, scopeType,
// scopeId})`.
//
// Scope modes:
//   - Org — whole organization. scopeId is null. Default for infra kinds
//     (github, google_workspace, npm).
//   - Workspace — a specific workspace in the org. scopeId = workspaceId.
//     Sources from root.workspaces (flat list) → root.jwtClaims.workspaces
//     (fallback). Filtered in real-time by a search input (§I12).
//   - Project — a specific project. scopeId = projectId. Sources from
//     root.projects (canvas-loaded flat list) → root._scopeProjectsByOrg
//     (governance lazy-fetch). Both sources normalised to { id, key, name }.
//
// Usage in a page:
//
//   IntegrationScopePicker: {
//     draftKey: '_newSupabaseDraft',     // required
//     defaultScopeType: 'project'        // from kind.default_scope
//   }

// SDK-only transport (integrations remediation #6) — this file's raw
// `fetch` against /projects (plus its hand-rolled channel base + localStorage
// token read) was one of the three raw-fetch leaves blocking the
// response-envelope normalisation. `sdk.getProjects` speaks the same
// contract (owner + limit) through the TokenManager.
import { getSDK } from '../functions/sdk.js'

// ── Search-list item ────────────────────────────────────────────────────

export const IntegrationScopeListItem = {
  tag: 'button',
  type: 'button',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 'Z',
  align: 'center left',
  width: '100%',
  padding: 'V Z1',
  border: 'none',
  // A picked row is the house SELECTED state (DESIGN_LANGUAGE §8.1: the
  // `selected` wash + `title` + 700; lists keep the wash, hue never marks a
  // state — admin audit 2026-09-24). It was a blue tint and blue ink through
  // inline factories, and the static :hover had to beat that inline fill, so
  // the framework emitted !important. Now a class rule keyed on the row's own
  // aria-pressed; the rule order keeps the picked row's wash under the pointer.
  ariaPressed: (el, s) => (s._selected ? 'true' : 'false'),
  background: 'transparent',
  color: 'title',
  cursor: 'pointer',
  fontSize: 'Z2',
  textAlign: 'left',
  ':hover': { background: 'hover' },
  '&[aria-pressed="true"]': {
    background: 'selected',
    fontWeight: '700',
    ':hover': { background: 'selected' }
  },

  Label: {
    tag: 'span',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s._label || ''
  },

  Check: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'title',
    hide: (el, s) => !s._selected,
    text: '✓'
  },

  // Generic click handler: reads _draftKey, _scopeType, _value from state.
  // Workspace list sets _scopeType:'workspace', project list sets 'project'.
  onClick: (e, el, s) => {
    const dk = s._draftKey
    if (!dk) return
    const type = s._scopeType || 'project'
    const clearKey = type === 'workspace' ? '_scopeWsQuery' : '_scopeProjQuery'
    s.root.update({
      [dk]: {
        ...(s.root?.[dk] || {}),
        scopeType: type,
        scopeId: s._value || null,
        _scopeManualMode: false
      },
      [clearKey]: ''
    })
  }
}

// ── Main export ────────────────────────────────────────────────────────

export const IntegrationScopePicker = {
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope const/function silently reads as undefined
  // once this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` from any descendant factory/handler. Regular
  // method syntax (not arrows) throughout — `el.scope.foo()` binds `this`
  // to `el.scope`, so a sibling helper calling another does it via
  // `this.<name>(...)` (object-literal properties can't see each other as
  // bare identifiers otherwise). `isActiveMode` (used only by the sibling
  // `IntegrationScopeModeBtn` export below) stays in THAT export's own
  // scope — `el.scope` doesn't cross top-level export boundaries.
  scope: {
    // Resolve the workspace list from the two possible root sources.
    // Priority: root.workspaces (flat array) → jwtClaims.workspaces.
    getWorkspaces(root) {
      const orgId = root?.jwtClaims?.workspace_org_id
      const list = Array.isArray(root?.workspaces)
        ? root.workspaces
        : root?.jwtClaims?.workspaces || []
      return orgId ? list.filter((w) => !w.org_id || w.org_id === orgId) : list
    },

    // Resolve the project list from the two possible root sources.
    // Priority: root.projects (canvas-loaded flat array) →
    //           root._scopeProjectsByOrg[orgId] (governance lazy-fetch).
    getProjects(root) {
      const orgId = root?.jwtClaims?.workspace_org_id

      // Canvas context: root.projects is the full flat project list.
      if (Array.isArray(root?.projects) && root.projects.length) {
        return root.projects.map((p) => ({
          id: p.id || p._id || p.key,
          key: p.key || p.slug,
          name: p.name || p.displayName || p.key
        }))
      }

      // Governance context: per-org API cache.
      if (orgId) {
        const cache = (root?._scopeProjectsByOrg || {})[orgId]
        if (Array.isArray(cache)) return cache
      }

      return []
    },

    // Case-insensitive substring filter. Checks `name` and `key` (key may be
    // absent for workspace items — that's fine, the OR short-circuits).
    filterItems(items, q) {
      if (!q) return items
      const lq = q.toLowerCase()
      return items.filter(
        (it) =>
          (it.name || it.label || '').toLowerCase().includes(lq) ||
          (it.key || '').toLowerCase().includes(lq)
      )
    },

    // ── Lazy project-load (governance context) ───────────────────────────
    async ensureProjectsLoaded(root, { force = false } = {}) {
      if (typeof window === 'undefined') return
      const orgId = root?.jwtClaims?.workspace_org_id
      if (!orgId) {
        this.startWatcher(root)
        return
      }
      const cache = root._scopeProjectsByOrg || {}
      if (!force && Array.isArray(cache[orgId])) return

      if (root._scopeProjectsInFlight?.[orgId]) return
      root._scopeProjectsInFlight = { ...(root._scopeProjectsInFlight || {}), [orgId]: true }

      const clearFlight = () => {
        const flight = { ...(root._scopeProjectsInFlight || {}) }
        delete flight[orgId]
        root._scopeProjectsInFlight = flight
      }

      const orgs = root?.jwtClaims?.orgs || []
      const org = orgs.find((o) => (o.id || o._id) === orgId)
      const slug = org?.slug || org?.username
      if (!slug) {
        clearFlight()
        this.startWatcher(root)
        return
      }

      let items = []
      let errorMsg = null
      try {
        // The SDK's TokenManager owns auth (no localStorage read here) and the
        // channel resolver owns the base URL — a signed-out caller surfaces as a
        // rejected call, handled by the catch below just like any transport
        // failure.
        const res = await getSDK().getProjects({ owner: slug, limit: 100 })
        // Tolerate every envelope the route has historically returned
        // ({data}, {items}, or a bare array) — the same shapes the raw reader
        // accepted, so normalising the server envelope later can't break this.
        const rows = Array.isArray(res) ? res : res?.data || res?.items || []
        items = rows.map((p) => ({
          id: p.id || p._id,
          name: p.name || p.displayName || p.key,
          key: p.key || p.slug
        }))
      } catch (err) {
        const status = err?.status || err?.statusCode
        errorMsg =
          status === 429
            ? 'apps.scope.error.rateLimited'
            : err?.message || 'apps.scope.error.fetchFailed'
      } finally {
        root.update({
          _scopeProjectsByOrg: {
            ...(root._scopeProjectsByOrg || {}),
            [orgId]: items
          },
          _scopeProjectsError: {
            ...(root._scopeProjectsError || {}),
            [orgId]: errorMsg
          }
        })
        clearFlight()
      }
    },

    startWatcher(root) {
      if (typeof window === 'undefined') return
      if (root._scopeProjectsWatcher) return
      const started = Date.now()
      root._scopeProjectsWatcher = setInterval(() => {
        const orgId = root?.jwtClaims?.workspace_org_id
        const orgs = root?.jwtClaims?.orgs || []
        const hasSlug = orgs.some((o) => (o.id || o._id) === orgId && (o.slug || o.username))
        if (orgId && hasSlug) {
          this.stopWatcher(root)
          this.ensureProjectsLoaded(root)
        } else if (Date.now() - started > 30_000) {
          this.stopWatcher(root)
          if (orgId) {
            root.update({
              _scopeProjectsByOrg: {
                ...(root._scopeProjectsByOrg || {}),
                [orgId]: []
              },
              _scopeProjectsError: {
                ...(root._scopeProjectsError || {}),
                [orgId]: 'apps.scope.error.sessionNotLoaded'
              }
            })
          }
        }
      }, 1000)
    },

    stopWatcher(root) {
      if (root?._scopeProjectsWatcher) {
        clearInterval(root._scopeProjectsWatcher)
        root._scopeProjectsWatcher = null
      }
    }
  },
  flow: 'y',
  gap: 'V',

  onDestroy: (el, s) => el.scope.stopWatcher(s?.root),

  Label: {
    tag: 'label',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '700',
    text: '{{ apps.scope.title | polyglot }}'
  },

  Hint: {
    tag: 'p',
    fontSize: 'Y2',
    color: 'caption',
    margin: '0 0 V 0',
    text: '{{ apps.scope.hint | polyglot }}'
  },

  Modes: {
    flow: 'x',
    gap: 'Z',
    childExtends: 'IntegrationScopeModeBtn',
    childrenAs: 'state',
    children: (el, s) => {
      const draftKey = el?.parent?.draftKey || s.draftKey
      return ['org', 'workspace', 'project'].map((type) => ({
        type,
        label:
          type === 'org'
            ? 'apps.scope.mode.org'
            : type === 'workspace'
              ? 'apps.scope.mode.workspace'
              : 'apps.scope.mode.project',
        draftKey
      }))
    }
  },

  // ── Workspace search-dropdown (§I12) ──────────────────────────────
  WorkspaceDropdown: {
    hide: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      return (draft.scopeType || s.defaultScopeType || 'org') !== 'workspace'
    },
    marginTop: 'V',
    flow: 'y',
    gap: 'V',

    // Selected-value badge — shows the chosen workspace name above the list.
    SelectedBadge: {
      tag: 'span',
      padding: 'V Z',
      round: 'radiusPill',
      fontSize: 'Y1',
      fontWeight: '700',
      // the neutral plate (was a blue tint painted as a raw rgba literal)
      background: 'widgetPlate',
      color: 'title',
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {}).scopeId
      },
      text: (el, s) => {
        const dk = s.draftKey
        if (!dk) return ''
        const scopeId = (s.root?.[dk] || {}).scopeId
        if (!scopeId) return ''
        const ws = el.scope.getWorkspaces(s.root)
        const found = ws.find((w) => (w.id || w.workspace_id || w._id) === scopeId)
        return el
          .call('polyglot', 'apps.scope.selected')
          .replace('{name}', found ? found.name || found.id : String(scopeId).slice(0, 10) + '…')
      }
    },

    SearchInput: {
      tag: 'input',
      type: 'text',
      width: '100%',
      theme: 'field',
      minHeight: 'controlH',
      round: 'radiusControl',
      color: 'title',
      fontSize: 'Z2',
      padding: '0 A',
      placeholder: (el) => el.call('polyglot', 'apps.scope.searchWorkspaces'),
      value: (el, s) => s.root?._scopeWsQuery || '',
      onInput: (e, el, s) => s.root.update({ _scopeWsQuery: e.target.value })
    },

    List: {
      flow: 'y',
      maxHeight: 'E',
      overflowY: 'auto',
      round: 'radiusControl',
      // Fill-contrast dropdown (was a `line` border — border-habit sweep,
      // design-direction.md §8) — the list's own `document` fill already
      // separates it from the page.
      background: 'document',

      childExtends: 'IntegrationScopeListItem',
      childrenAs: 'state',
      children: (el, s) => {
        const dk = s.draftKey
        const q = s.root?._scopeWsQuery || ''
        const curId = dk ? (s.root?.[dk] || {}).scopeId || '' : ''
        const ws = el.scope.getWorkspaces(s.root).map((w) => ({
          id: w.id || w.workspace_id || w._id,
          name: w.name || w.id || w.workspace_id
        }))
        const filtered = el.scope.filterItems(ws, q)
        return filtered.map((it) => ({
          _value: it.id,
          _label: it.name,
          _selected: it.id === curId,
          _draftKey: dk,
          _scopeType: 'workspace'
        }))
      },

      Empty: {
        tag: 'span',
        padding: 'V Z1',
        fontSize: 'Z2',
        color: 'caption',
        fontStyle: 'italic',
        hide: (el, s) => {
          const dk = s.draftKey
          const q = s.root?._scopeWsQuery || ''
          const ws = el.scope.getWorkspaces(s.root)
          return el.scope.filterItems(ws, q).length > 0
        },
        text: (el, s) => {
          const q = s.root?._scopeWsQuery || ''
          const total = el.scope.getWorkspaces(s.root).length
          if (!total) return el.call('polyglot', 'apps.scope.ws.emptyOrg')
          return q
            ? el.call('polyglot', 'apps.scope.noMatches').replace('{query}', q)
            : el.call('polyglot', 'apps.scope.ws.empty')
        }
      }
    }
  },

  // ── Project search-dropdown (§I12) ────────────────────────────────
  ProjectDropdown: {
    hide: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      return (draft.scopeType || s.defaultScopeType || 'org') !== 'project'
    },
    marginTop: 'V',
    flow: 'y',
    gap: 'V',

    // Trigger API fetch if root.projects is empty (governance context).
    onRender: (el, s) => {
      const draft = s.root?.[s.draftKey] || {}
      const active = (draft.scopeType || s.parent?.defaultScopeType || 'org') === 'project'
      if (active && !(Array.isArray(s.root?.projects) && s.root.projects.length)) {
        el.scope.ensureProjectsLoaded(s.root)
      }
    },

    // Selected-value badge.
    SelectedBadge: {
      tag: 'span',
      padding: 'V Z',
      round: 'radiusPill',
      fontSize: 'Y1',
      fontWeight: '700',
      // the neutral plate (was a blue tint painted as a raw rgba literal)
      background: 'widgetPlate',
      color: 'title',
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {}).scopeId
      },
      text: (el, s) => {
        const dk = s.draftKey
        if (!dk) return ''
        const scopeId = (s.root?.[dk] || {}).scopeId
        if (!scopeId) return ''
        const projects = el.scope.getProjects(s.root)
        const found = projects.find((p) => (p.id || p.key) === scopeId)
        return el
          .call('polyglot', 'apps.scope.selected')
          .replace('{name}', found ? found.name || found.key : String(scopeId).slice(0, 10) + '…')
      }
    },

    // Loading / error hints.
    LoadingHint: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      fontStyle: 'italic',
      hide: (el, s) => {
        if (Array.isArray(s.root?.projects) && s.root.projects.length) return true
        const orgId = s.root?.jwtClaims?.workspace_org_id
        return orgId && Array.isArray((s.root?._scopeProjectsByOrg || {})[orgId])
      },
      text: '{{ apps.scope.loadingProjects | polyglot }}'
    },

    ErrorHint: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'red',
      hide: (el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        return !orgId || !(s.root?._scopeProjectsError || {})[orgId]
      },
      text: (el, s) => {
        const orgId = s.root?.jwtClaims?.workspace_org_id
        const err = orgId ? (s.root?._scopeProjectsError || {})[orgId] : null
        // `err` is either a bundle KEY written by `ensureProjectsLoaded` /
        // `startWatcher` (neither has an element) or a raw server message.
        // polyglot resolves the first and passes the second through.
        return err
          ? el
              .call('polyglot', 'apps.scope.proj.loadFailed')
              .replace('{error}', el.call('polyglot', err))
          : ''
      }
    },

    SearchInput: {
      tag: 'input',
      type: 'text',
      width: '100%',
      theme: 'field',
      minHeight: 'controlH',
      round: 'radiusControl',
      color: 'title',
      fontSize: 'Z2',
      padding: '0 A',
      placeholder: (el) => el.call('polyglot', 'apps.scope.searchProjects'),
      value: (el, s) => s.root?._scopeProjQuery || '',
      onInput: (e, el, s) => s.root.update({ _scopeProjQuery: e.target.value })
    },

    List: {
      flow: 'y',
      maxHeight: 'E',
      overflowY: 'auto',
      round: 'radiusControl',
      // Fill-contrast dropdown (was a `line` border — border-habit sweep,
      // design-direction.md §8) — the list's own `document` fill already
      // separates it from the page.
      background: 'document',

      childExtends: 'IntegrationScopeListItem',
      childrenAs: 'state',
      children: (el, s) => {
        const dk = s.draftKey
        const q = s.root?._scopeProjQuery || ''
        const curId = dk ? (s.root?.[dk] || {}).scopeId || '' : ''
        const projects = el.scope.getProjects(s.root)
        const filtered = el.scope.filterItems(projects, q)
        return filtered.map((p) => ({
          _value: p.id || p.key,
          _label: p.name || p.key || p.id,
          _selected: (p.id || p.key) === curId,
          _draftKey: dk,
          _scopeType: 'project'
        }))
      },

      Empty: {
        tag: 'span',
        padding: 'V Z1',
        fontSize: 'Z2',
        color: 'caption',
        fontStyle: 'italic',
        hide: (el, s) => {
          const q = s.root?._scopeProjQuery || ''
          const projects = el.scope.getProjects(s.root)
          // Hide empty message while loading.
          if (
            !(Array.isArray(s.root?.projects) && s.root.projects.length) &&
            !Array.isArray((s.root?._scopeProjectsByOrg || {})[s.root?.jwtClaims?.workspace_org_id])
          ) {
            return true
          }
          return el.scope.filterItems(projects, q).length > 0
        },
        text: (el, s) => {
          const q = s.root?._scopeProjQuery || ''
          const total = el.scope.getProjects(s.root).length
          if (!total) return el.call('polyglot', 'apps.scope.proj.emptyOrg')
          return q
            ? el.call('polyglot', 'apps.scope.noMatches').replace('{query}', q)
            : el.call('polyglot', 'apps.scope.proj.empty')
        }
      }
    },

    // Toggle between list and manual-ID entry.
    ManualEntryToggle: {
      tag: 'button',
      type: 'button',
      padding: 'V Z',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y1',
      color: 'caption',
      textDecoration: 'underline',
      textAlign: 'left',
      text: (el, s) => {
        const dk = s.draftKey
        return el.call(
          'polyglot',
          (s.root?.[dk] || {})._scopeManualMode
            ? 'apps.scope.proj.backToList'
            : 'apps.scope.proj.manualEntry'
        )
      },
      onClick: (e, el, s) => {
        const dk = s.draftKey
        if (!dk) return
        const current = s.root?.[dk] || {}
        s.root.update({
          [dk]: { ...current, _scopeManualMode: !current._scopeManualMode }
        })
      }
    },

    ManualEntryRow: {
      hide: (el, s) => {
        const dk = s.draftKey
        return !dk || !(s.root?.[dk] || {})._scopeManualMode
      },
      flow: 'y',
      gap: 'V',
      Label: {
        tag: 'label',
        fontSize: 'Y1',
        color: 'caption',
        text: '{{ apps.scope.projectId | polyglot }}'
      },
      Input: {
        tag: 'input',
        type: 'text',
        width: '100%',
        theme: 'field',
        minHeight: 'controlH',
        round: 'radiusControl',
        color: 'title',
        fontSize: 'Z2',
        padding: '0 A',
        placeholder: 'e.g. prj_abc123',
        value: (el, s) => (s.root?.[s.draftKey] || {}).scopeId || '',
        onInput: (e, el, s) => {
          const dk = s.draftKey
          if (!dk) return
          s.root.update({
            [dk]: {
              ...(s.root?.[dk] || {}),
              scopeType: 'project',
              scopeId: e.target.value || null,
              _scopeManualMode: true
            }
          })
        }
      }
    }
  }
}

export const IntegrationScopeOption = {
  tag: 'option',
  // `label` is a KEY-IN-STATE carrier: workspace/pages/admin/module.js feeds
  // `admin.module.tier.trusted` / `.sandboxed` / `admin.module.version.latest`
  // here, and used to ship those raw dotted keys straight into the <option>.
  // A version string (`label: val`) is not a key and passes through unchanged.
  text: (el, s) => el.call('polyglot', s.label || ''),
  onCreate: (el, s) => {
    if (el.node) el.node.value = s.value == null ? '' : s.value
  },
  onUpdate: (el, s) => {
    if (el.node) el.node.value = s.value == null ? '' : s.value
  }
}

export const IntegrationScopeModeBtn = {
  // FA204 / frank trap (see IntegrationScopePicker above) — isActiveMode
  // used to be a bare module-scope const shared with IntegrationScopePicker's
  // helpers; scoped here instead since `el.scope` doesn't cross top-level
  // export boundaries and this is the only export that reads it.
  scope: {
    // Shared by background/color/hover factories below (hoisted while fixing
    // the border-habit site so the hover addition doesn't triplicate the same
    // active check a third time).
    isActiveMode(s) {
      const parent = s.parent || {}
      const draft = s.root?.[s.draftKey] || {}
      return (draft.scopeType || parent.defaultScopeType || 'org') === s.type
    }
  },
  tag: 'button',
  type: 'button',
  minHeight: 'controlHCompact',
  padding: '0 Z',
  round: 'radiusPill',
  fontSize: 'Z',
  fontWeight: '700',
  cursor: 'pointer',
  // A CHOICE control (Nika 2026-09-23, D-1; admin audit 2026-09-24): the
  // chosen mode is the inversion — `highlight` + `highlight-reversed` — and
  // says so with aria-pressed. The idle modes are the house tonal chip
  // (`title` at 14 / 22 / 30%). It was a blue fill through inline factories,
  // including a reactive :hover the framework could only apply with
  // !important (54 declarations on /admin/integrations/github). Every state
  // is a class rule now; the chosen block comes last so it keeps its fill
  // under the pointer.
  ariaPressed: (el, s) => (el.scope.isActiveMode(s) ? 'true' : 'false'),
  color: 'title',
  background: 'title.14',
  ':hover': { background: 'title.22' },
  ':active': { background: 'title.3' },
  '&[aria-pressed="true"]': {
    background: 'highlight',
    color: 'highlight-reversed',
    ':hover': { background: 'highlight' },
    ':active': { background: 'highlight' }
  },
  // KEY-IN-STATE contract. The three mode labels above still ship English:
  // their keys belong in `apps.json`, which this lane does not own.
  text: (el, s) => el.call('polyglot', s.label || ''),
  onClick: (e, el, s) => {
    s.root.update({
      [s.draftKey]: {
        ...(s.root?.[s.draftKey] || {}),
        scopeType: s.type,
        scopeId: s.type === 'org' ? null : (s.root?.[s.draftKey] || {}).scopeId || null
      },
      // Clear search queries on mode switch.
      _scopeWsQuery: '',
      _scopeProjQuery: ''
    })
  }
}

// Read `{scopeType, scopeId}` off a draft, supplying defaults. Use this
// in page save handlers so you don't repeat the fallback logic.
export const resolveScope = (draft, defaultScopeType = 'org') => {
  const scopeType = draft?.scopeType || defaultScopeType
  if (scopeType === 'org') return { scopeType: 'org', scopeId: null }
  return { scopeType, scopeId: draft?.scopeId || null }
}

// Tiny scope badge for instance cards. Reads `s.scope_type` / `s.scope_id`.
export const IntegrationScopeBadge = {
  tag: 'span',
  padding: 'V Z',
  round: 'radiusPill',
  fontSize: 'Y1',
  fontWeight: '700',
  // the neutral plate: `overlayStrong` is a sheet wash, not a plate — it read
  // about 1:1 on a card
  background: 'widgetPlate',
  color: 'caption',
  text: (el, s) => {
    const t = (k) => el.call('polyglot', k)
    if (!s.scope_type || s.scope_type === 'org') return t('apps.scope.mode.org')
    const label = t(
      s.scope_type === 'workspace' ? 'apps.scope.mode.workspace' : 'apps.scope.mode.project'
    )
    const short = s.scope_id ? `: ${String(s.scope_id).slice(0, 6)}…` : ''
    return `${label}${short}`
  }
}
